import { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  boschBlue,
  boschGray,
  boschGreen,
  boschRed,
} from '../../modules/theme/boschColors';
import { useApiData } from './api/useApiData';

/**
 * Content-page widget showing the signed-in user's recent
 * notifications inbox. Same data as the bell icon in the header,
 * but inline on a page so a homepage or team space can surface
 * "what happened lately" without forcing a click into the bell.
 *
 * Endpoint
 * --------
 *   GET /api/notifications/notifications?limit=N&read=false
 *
 * The notifications-backend resolves the user from the request's
 * session cookie automatically — we don't pass a user ref. This
 * means a signed-in user sees ONLY their own notifications (same
 * as the bell), so embedding this widget on a public homepage is
 * safe — anonymous visitors get an empty list, not a 401.
 *
 * Response shape (from `plugin-notifications-backend`):
 *
 *   {
 *     totalCount: number,
 *     notifications: [
 *       {
 *         id: string,
 *         user: string | null,
 *         created: string,            // ISO
 *         read: string | null,        // ISO when read, null when not
 *         origin: string,
 *         payload: {
 *           title: string,
 *           description?: string,
 *           link?: string,
 *           severity?: 'low' | 'normal' | 'high' | 'critical',
 *           topic?: string,
 *         }
 *       },
 *       ...
 *     ]
 *   }
 *
 * Visual:
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ RECENT NOTIFICATIONS                            ▶ See all │
 *   │                                                            │
 *   │ ● Group membership changed: Alice                         │
 *   │   Probe failed (HTTP 500) · 2m ago                        │
 *   │                                                            │
 *   │ ● New component: payment-service                          │
 *   │   1h ago                                                  │
 *   │                                                            │
 *   │ ○ User role updated: Maria Souza                          │
 *   │   Yesterday                                               │
 *   └──────────────────────────────────────────────────────────┘
 *
 * The dot is filled when unread, hollow when read.
 */

interface NotificationDto {
  id: string;
  user: string | null;
  created: string;
  read: string | null;
  origin: string;
  payload: {
    title: string;
    description?: string;
    link?: string;
    severity?: 'low' | 'normal' | 'high' | 'critical';
    topic?: string;
  };
}

interface NotificationsResponse {
  totalCount: number;
  notifications: NotificationDto[];
}

export interface RecentNotificationsWidgetProps {
  title?: string;
  limit?: number;
  unreadOnly?: boolean;
  refreshSeconds?: number;
  /** Show a "See all" deep link to /notifications. Defaults to true. */
  showSeeAll?: boolean;
}

const useStyles = makeStyles(theme => ({
  card: {
    border: `1px solid ${boschGray[90]}`,
    borderRadius: 6,
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    color: boschGray[20],
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    flex: 1,
  },
  seeAll: {
    fontSize: 12,
    fontWeight: 600,
    color: boschBlue[50],
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  row: {
    display: 'flex',
    gap: theme.spacing(1.25),
    padding: theme.spacing(0.75, 0),
    borderTop: `1px solid ${boschGray[95]}`,
  },
  rowFirst: {
    borderTop: 'none',
  },
  /*
   * Severity dot. Filled when unread, hollow ring when read — same
   * convention every email client uses, so users don't have to
   * learn a new visual language to read the list.
   */
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginTop: 4,
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: boschGray[20],
    lineHeight: 1.3,
    /*
     * Keep the title to a single line; titles can be 80+ chars
     * (e.g. "Uptime DOWN: long-monitoring-target-name") and a
     * dashboard tile shouldn't wrap them across 3 lines.
     */
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemMeta: {
    fontSize: 11,
    color: boschGray[40],
    marginTop: 2,
  },
  itemLink: {
    color: boschGray[20],
    textDecoration: 'none',
    '&:hover': {
      color: boschBlue[50],
      textDecoration: 'underline',
    },
  },
  state: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    color: boschGray[40],
    fontSize: 13,
  },
  empty: {
    color: boschGray[50],
    fontSize: 13,
    padding: theme.spacing(1, 0),
    textAlign: 'center',
  },
}));

const severityColor = (
  severity: NotificationDto['payload']['severity'],
): string => {
  switch (severity) {
    case 'critical':
      return boschRed[40];
    case 'high':
      return boschRed[50];
    case 'normal':
      return boschBlue[50];
    case 'low':
      return boschGreen[50];
    default:
      return boschGray[70];
  }
};

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const diffSec = Math.round((Date.now() - at.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  const days = Math.round(diffSec / 86400);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return at.toLocaleDateString();
};

export const RecentNotificationsWidget = (
  props: RecentNotificationsWidgetProps,
) => {
  const classes = useStyles();
  const title = props.title?.trim() || 'Recent notifications';
  const limit = Math.max(1, Math.min(50, props.limit ?? 5));
  const unreadOnly = Boolean(props.unreadOnly);
  const showSeeAll = props.showSeeAll !== false;
  const refreshSeconds = props.refreshSeconds && props.refreshSeconds > 0
    ? props.refreshSeconds
    : undefined;

  /*
   * The notifications-backend exposes the same handler at both
   * `/` and `/notifications` — the latter is the canonical path
   * used by the Backstage notifications-react package. We use it
   * here to keep the network tab tidy alongside the bell.
   *
   * `read=false` returns ONLY unread; we omit the param entirely
   * when the user wants the full list (the backend doesn't filter
   * either way when the param is missing).
   */
  const url = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (unreadOnly) params.set('read', 'false');
    return `/api/notifications/notifications?${params.toString()}`;
  }, [limit, unreadOnly]);

  const result = useApiData({ url, refreshSeconds });
  const data = result.raw as NotificationsResponse | undefined;
  const items = data?.notifications ?? [];

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent>
        <Box className={classes.header}>
          <Typography component="div" className={classes.title}>
            {title}
            {data && data.totalCount > 0 && (
              <> ({data.totalCount})</>
            )}
          </Typography>
          {showSeeAll && (
            <Link href="/notifications" className={classes.seeAll}>
              See all →
            </Link>
          )}
        </Box>

        {result.loading && !data ? (
          <Box className={classes.state}>
            <CircularProgress size={16} style={{ color: boschBlue[50] }} />
            <span>Loading…</span>
          </Box>
        ) : result.error ? (
          <Typography component="div" className={classes.state}>
            Failed to load: {result.error.message}
          </Typography>
        ) : items.length === 0 ? (
          <Typography component="div" className={classes.empty}>
            {unreadOnly
              ? 'No unread notifications. You are all caught up.'
              : 'No notifications yet.'}
          </Typography>
        ) : (
          items.map((n, idx) => {
            const isUnread = !n.read;
            const color = severityColor(n.payload.severity);
            const TitleEl = n.payload.link ? (
              <Link href={n.payload.link} className={classes.itemLink}>
                {n.payload.title}
              </Link>
            ) : (
              <>{n.payload.title}</>
            );
            return (
              <Box
                key={n.id}
                className={`${classes.row} ${
                  idx === 0 ? classes.rowFirst : ''
                }`}
              >
                <span
                  className={classes.dot}
                  style={{
                    background: isUnread ? color : 'transparent',
                    border: isUnread ? 'none' : `2px solid ${color}`,
                  }}
                  aria-label={isUnread ? 'unread' : 'read'}
                />
                <div className={classes.body}>
                  <div
                    className={classes.itemTitle}
                    title={n.payload.title}
                  >
                    {TitleEl}
                  </div>
                  <div className={classes.itemMeta}>
                    {n.payload.topic && (
                      <>{n.payload.topic} · </>
                    )}
                    {formatRelative(n.created)}
                  </div>
                </div>
              </Box>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
